import type { StockQuote, FMPApiResponse, MarketHours, HistoricalPrice, CompanyProfile, IntradayCandle } from './types'

class FMPApiService {
  private apiKey: string
  private baseUrl = 'https://financialmodelingprep.com/stable'

  constructor(apiKey: string) {
    this.apiKey = apiKey
  }

  private async makeRequest<T>(endpoint: string): Promise<FMPApiResponse<T>> {
    try {
      const url = `${this.baseUrl}${endpoint}${endpoint.includes('?') ? '&' : '?'}apikey=${this.apiKey}`
      const response = await fetch(url)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      
      return {
        success: true,
        data: data
      }
    } catch (error) {
      return {
        success: false,
        data: null as T,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async getQuote(symbol: string): Promise<FMPApiResponse<StockQuote[]>> {
    const singleUrl = `https://financialmodelingprep.com/api/v3/quote/${symbol.toUpperCase()}?apikey=${this.apiKey}`
    try {
      const response = await fetch(singleUrl)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const rawData: any[] = await response.json()
      const data: StockQuote[] = (Array.isArray(rawData) ? rawData : []).map((item: any) => ({
        symbol: String(item.symbol ?? ''),
        name: String(item.name ?? ''),
        price: Number(item.price ?? 0),
        changePercentage: Number((item.changesPercentage ?? item.changePercentage) ?? 0),
        change: Number(item.change ?? 0),
        volume: Number(item.volume ?? 0),
        dayLow: Number(item.dayLow ?? 0),
        dayHigh: Number(item.dayHigh ?? 0),
        yearHigh: Number(item.yearHigh ?? 0),
        yearLow: Number(item.yearLow ?? 0),
        marketCap: Number(item.marketCap ?? 0),
        priceAvg50: Number(item.priceAvg50 ?? 0),
        priceAvg200: Number(item.priceAvg200 ?? 0),
        exchange: String(item.exchange ?? ''),
        open: Number(item.open ?? 0),
        previousClose: Number(item.previousClose ?? 0),
        timestamp: Number(item.timestamp ?? Date.now())
      }))
      return { success: true, data }
    } catch (error) {
      return {
        success: false,
        data: [] as unknown as StockQuote[],
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async getQuotes(symbols: string[]): Promise<FMPApiResponse<StockQuote[]>> {
    const symbolsString = symbols.map(s => s.toUpperCase()).join(',')
    const url = `https://financialmodelingprep.com/api/v3/quote/${symbolsString}?apikey=${this.apiKey}`
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const rawData: any[] = await response.json()
      const data: StockQuote[] = (Array.isArray(rawData) ? rawData : []).map((item: any) => ({
        symbol: String(item.symbol ?? ''),
        name: String(item.name ?? ''),
        price: Number(item.price ?? 0),
        changePercentage: Number((item.changesPercentage ?? item.changePercentage) ?? 0),
        change: Number(item.change ?? 0),
        volume: Number(item.volume ?? 0),
        dayLow: Number(item.dayLow ?? 0),
        dayHigh: Number(item.dayHigh ?? 0),
        yearHigh: Number(item.yearHigh ?? 0),
        yearLow: Number(item.yearLow ?? 0),
        marketCap: Number(item.marketCap ?? 0),
        priceAvg50: Number(item.priceAvg50 ?? 0),
        priceAvg200: Number(item.priceAvg200 ?? 0),
        exchange: String(item.exchange ?? ''),
        open: Number(item.open ?? 0),
        previousClose: Number(item.previousClose ?? 0),
        timestamp: Number(item.timestamp ?? Date.now())
      }))
      return { success: true, data }
    } catch (error) {
      return {
        success: false,
        data: [] as unknown as StockQuote[],
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async searchSymbol(query: string): Promise<FMPApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/search-symbol?query=${encodeURIComponent(query)}`)
  }

  async searchName(query: string): Promise<FMPApiResponse<any[]>> {
    return this.makeRequest<any[]>(`/search-name?query=${encodeURIComponent(query)}`)
  }

  async getMarketHours(): Promise<FMPApiResponse<MarketHours[]>> {
    return this.makeRequest<MarketHours[]>(`/all-exchange-market-hours`)
  }

  async getExchangeMarketHours(exchange: string): Promise<FMPApiResponse<MarketHours[]>> {
    // FMP returns an array for exchange-specific market hours
    return this.makeRequest<MarketHours[]>(`/exchange-market-hours?exchange=${exchange.toUpperCase()}`)
  }

  async getHistoricalPrice(symbol: string, from: string, to: string): Promise<FMPApiResponse<HistoricalPrice[]>> {
    return this.makeRequest<HistoricalPrice[]>(`/historical-price-eod/full?symbol=${symbol.toUpperCase()}&from=${from}&to=${to}`)
  }
  
  // Intraday historical chart for intervals: 1min, 5min, 15min, 30min, 1hour, 4hour
  async getHistoricalIntraday(
    symbol: string,
    interval: '1min' | '5min' | '15min' | '30min' | '1hour' | '4hour',
    from?: string,
    to?: string
  ): Promise<FMPApiResponse<IntradayCandle[]>> {
    // Endpoint: /historical-chart/{interval}?symbol=SYMB&from=YYYY-MM-DD&to=YYYY-MM-DD
    const params: string[] = [`symbol=${symbol.toUpperCase()}`]
    if (from) params.push(`from=${from}`)
    if (to) params.push(`to=${to}`)
    const qs = params.join('&')
  console.log(`/historical-chart/${interval}?${qs}`)
    return this.makeRequest<IntradayCandle[]>(`/historical-chart/${interval}?${qs}`)
  }
  
  // Legacy/v3 Daily Chart EOD shape: { symbol, historical: [...] }
  async getHistoricalPriceOld(symbol: string, from?: string, to?: string, ): Promise<FMPApiResponse<{ symbol: string; historical: any[] }>> {
    const base = `https://financialmodelingprep.com/api/v3/historical-price-full/${symbol.toUpperCase()}`
    const params: string[] = []
    if (from) params.push(`from=${from}`)
    if (to) params.push(`to=${to}`)
    const qs = params.length > 0 ? `?${params.join('&')}&apikey=${this.apiKey}` : `?apikey=${this.apiKey}`
    const url = `${base}${qs}`
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      return { success: true, data }
    } catch (error) {
      return {
        success: false,
        data: { symbol: symbol.toUpperCase(), historical: [] },
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
  

  // Company profile (stable API)
  async getCompanyProfile(symbol: string): Promise<FMPApiResponse<CompanyProfile[]>> {
    return this.makeRequest<CompanyProfile[]>(`/profile?symbol=${symbol.toUpperCase()}`)
  }
}

export default FMPApiService 